import * as React from 'react'
import { toast } from '@repo/studio-components-toast'
import ms from 'ms'
import { create } from 'zustand'
import { AIDA_COMPOSER_DRAFT_KEY } from '../utils/constants/storage-keys'
import { useAidaChatCommands } from './chat-commands'

export type ComposerNoticeTone = 'warning' | 'error' | 'info'

export interface ComposerNoticeAction {
  label: string
  onClick: () => void
}

export interface ComposerNotice {
  id: string
  tone: ComposerNoticeTone
  title: string
  description?: string
  action?: ComposerNoticeAction
  autoDismissMs?: number
  revision: number
}

export interface ComposerNoticeInput {
  id: string
  tone: ComposerNoticeTone
  title: string
  description?: string | null
  action?: ComposerNoticeAction
  autoDismissMs?: number
  conversationId?: string | null
}

interface ComposerNoticeState {
  notices: Record<string, ComposerNotice>
  mounted: number
}

export const COMPOSER_NOTICE_AUTO_DISMISS_MS = ms('6s')

export const useComposerNoticeStore = create<ComposerNoticeState>(() => ({
  notices: {},
  mounted: 0,
}))

export function composerNoticeKey(conversationId: string | null) {
  return conversationId ?? AIDA_COMPOSER_DRAFT_KEY
}

export function composerNoticeAutoDismissMs(notice: {
  tone: ComposerNoticeTone
  autoDismissMs?: number
}) {
  if (notice.autoDismissMs !== undefined) {
    return notice.autoDismissMs
  }
  if (notice.tone === 'error') {
    return null
  }
  return COMPOSER_NOTICE_AUTO_DISMISS_MS
}

export function retainComposerMount() {
  useComposerNoticeStore.setState(state => ({ mounted: state.mounted + 1 }))
  return () => {
    useComposerNoticeStore.setState(state => ({ mounted: state.mounted - 1 }))
  }
}

export function resetComposerNotices() {
  useComposerNoticeStore.setState({ notices: {}, mounted: 0 })
}

export function notifyComposer(input: ComposerNoticeInput) {
  const conversationId =
    input.conversationId !== undefined
      ? input.conversationId
      : useAidaChatCommands.getState().activeConversationId
  if (useComposerNoticeStore.getState().mounted === 0) {
    void toast({
      title: input.title,
      description: fallbackDescription(input),
    })
    return
  }
  showComposerNotice(composerNoticeKey(conversationId), input)
}

export function showComposerNotice(key: string, input: ComposerNoticeInput) {
  useComposerNoticeStore.setState(state => {
    const current = state.notices[key]
    const revision = current?.id === input.id ? current.revision + 1 : 1
    const notice: ComposerNotice = {
      id: input.id,
      tone: input.tone,
      title: input.title,
      revision,
      ...(input.description ? { description: input.description } : {}),
      ...(input.action ? { action: input.action } : {}),
      ...(input.autoDismissMs !== undefined ? { autoDismissMs: input.autoDismissMs } : {}),
    }
    return { notices: { ...state.notices, [key]: notice } }
  })
}

export function dismissComposerNotice(key: string, id?: string) {
  useComposerNoticeStore.setState(state => {
    const current = state.notices[key]
    if (!current) {
      return state
    }
    if (id && current.id !== id) {
      return state
    }
    const notices = Object.fromEntries(
      Object.entries(state.notices).filter(([noticeKey]) => noticeKey !== key),
    )
    return { notices }
  })
}

export function dismissComposerNoticeId(id: string) {
  useComposerNoticeStore.setState(state => {
    const notices = Object.fromEntries(
      Object.entries(state.notices).filter(([, notice]) => notice.id !== id),
    )
    if (Object.keys(notices).length === Object.keys(state.notices).length) {
      return state
    }
    return { notices }
  })
}

export function clearComposerNoticeOnSend(conversationId: string | null) {
  dismissComposerNotice(composerNoticeKey(conversationId))
}

function fallbackDescription(input: ComposerNoticeInput) {
  if (!input.action) {
    return input.description ?? null
  }
  const button = React.createElement(
    'button',
    { type: 'button', className: 'underline', onClick: input.action.onClick },
    input.action.label,
  )
  if (!input.description) {
    return button
  }
  return React.createElement('span', null, input.description, button)
}
